// src/components/admin/AdminNewsletter.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';

const SOURCE_LABELS = {
  homepage:  '🏠 Homepage',
  footer:    '🔻 Footer',
  signup:    '👤 Signup',
  checkout:  '🛒 Checkout',
  admin:     '⚙️ Admin',
};

export default function AdminNewsletter() {
  const { showToast } = useApp();
  const [subscribers, setSubscribers]   = useState([]);
  const [all,         setAll]           = useState([]);
  const [form,        setForm]          = useState({ subject: '', body: '' });
  const [sending,     setSending]       = useState(false);
  const [tab,         setTab]           = useState('compose');
  const [filter,      setFilter]        = useState('active');
  const [search,      setSearch]        = useState('');

  const load = async () => {
    const { data } = await supabase
      .from('newsletter_subscribers')
      .select('*')
      .order('subscribed_at', { ascending: false });
    setAll(data || []);
    setSubscribers((data || []).filter(s => s.active));
  };

  useEffect(() => { load(); }, []);

  const handleSend = async () => {
    if (!form.subject || !form.body) { showToast('Subject and body required', 'error'); return; }
    setSending(true);
    await supabase.from('push_notifications').insert({
      title:   form.subject,
      body:    form.body.slice(0, 200),
      sent_to: 'newsletter',
    });
    showToast(`Newsletter queued for ${subscribers.length} subscribers`, 'success');
    setForm({ subject: '', body: '' });
    setSending(false);
  };

  const unsubscribe = async (id, email) => {
    if (!window.confirm(`Unsubscribe ${email}?`)) return;
    await supabase.from('newsletter_subscribers').update({ active: false }).eq('id', id);
    showToast(`${email} unsubscribed`, 'info');
    load();
  };

  const resubscribe = async (id, email) => {
    await supabase.from('newsletter_subscribers').update({ active: true }).eq('id', id);
    showToast(`${email} resubscribed`, 'success');
    load();
  };

  const exportCSV = () => {
    const rows = [
      ['email', 'name', 'active', 'source', 'gdpr_consent', 'gdpr_consent_at', 'subscribed_at', 'country', 'language'],
      ...all.map(s => [
        s.email, s.name || '', s.active, s.source || '',
        s.gdpr_consent ? 'yes' : 'no', s.gdpr_consent_at || '',
        s.subscribed_at, s.country || '', s.language || '',
      ]),
    ];
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url;
    a.download = `cosmiqherbz-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Filtered + searched list
  const display = all
    .filter(s => filter === 'active' ? s.active : filter === 'inactive' ? !s.active : true)
    .filter(s => !search || s.email.toLowerCase().includes(search.toLowerCase()) || (s.name || '').toLowerCase().includes(search.toLowerCase()));

  // Stats
  const total    = all.length;
  const active   = all.filter(s => s.active).length;
  const gdprOk   = all.filter(s => s.gdpr_consent).length;
  const bySource = all.reduce((acc, s) => { acc[s.source || 'unknown'] = (acc[s.source || 'unknown'] || 0) + 1; return acc; }, {});

  return (
    <div className="page-container" style={{ padding: '32px 24px', maxWidth: 860 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.8rem', letterSpacing: '.05em' }}>Newsletter</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.8rem', color: '#4cca74', fontWeight: 700, alignSelf: 'center' }}>
            {active} active
          </span>
          <button onClick={exportCSV} style={{ background: 'rgba(76,202,116,.1)', border: '1px solid rgba(76,202,116,.25)', borderRadius: 20, padding: '6px 14px', color: '#4cca74', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem', fontWeight: 700, cursor: 'pointer' }}>
            ↓ Export CSV
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Total',       value: total,   color: '#f4f4f4' },
          { label: 'Active',      value: active,  color: '#4cca74' },
          { label: 'Unsubscribed', value: total - active, color: '#e05050' },
          { label: 'GDPR Consent', value: gdprOk, color: '#4cca74' },
        ].map(s => (
          <div key={s.label} style={{ background: '#111', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(255,255,255,.07)' }}>
            <div style={{ fontSize: '.68rem', color: '#555', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.6rem', color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Source breakdown */}
      {Object.keys(bySource).length > 0 && (
        <div style={{ background: '#111', borderRadius: 12, border: '1px solid rgba(255,255,255,.07)', padding: '14px 16px', marginBottom: 20 }}>
          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.68rem', fontWeight: 700, letterSpacing: '.15em', textTransform: 'uppercase', color: '#4cca74', marginBottom: 10 }}>Signup Sources</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {Object.entries(bySource).sort(([,a],[,b]) => b - a).map(([src, cnt]) => (
              <div key={src} style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.07)', borderRadius: 20, padding: '4px 12px', fontSize: '.75rem', color: '#ccc', fontFamily: "'Barlow Condensed',sans-serif" }}>
                {SOURCE_LABELS[src] || src} <strong style={{ color: '#4cca74' }}>{cnt}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#111', borderRadius: 12, padding: 4 }}>
        {['compose', 'subscribers'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: '9px', border: 'none', borderRadius: 8, background: tab === t ? '#4cca74' : 'none', color: tab === t ? '#000' : '#555', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.82rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Compose ── */}
      {tab === 'compose' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <div className="input-label">Subject Line</div>
            <input className="input-field" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="New drop: Cosmic OG is back!" />
          </div>
          <div style={{ marginBottom: 20 }}>
            <div className="input-label">Email Body</div>
            <textarea className="input-field" rows={12} value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} style={{ resize: 'vertical' }} placeholder="Write your newsletter here…" />
          </div>
          <div style={{ background: 'rgba(240,165,0,.06)', border: '1px solid rgba(240,165,0,.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 20, fontSize: '.8rem', color: 'rgba(240,165,0,.75)', lineHeight: 1.7 }}>
            ⚠️ Connect Mailchimp, Resend, or SendGrid in your serverless functions to send real emails. This panel queues the campaign in your logs.
          </div>
          <button className="btn-primary" style={{ width: '100%' }} onClick={handleSend} disabled={sending}>
            {sending
              ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2, margin: '0 auto' }} />
              : `Send to ${active} active subscribers`}
          </button>
        </div>
      )}

      {/* ── Subscribers ── */}
      {tab === 'subscribers' && (
        <>
          {/* Filter + search */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {['all', 'active', 'inactive'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ padding: '5px 14px', border: 'none', borderRadius: 20, background: filter === f ? 'rgba(76,202,116,.15)' : '#111', color: filter === f ? '#4cca74' : '#555', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
                {f} ({f === 'all' ? total : f === 'active' ? active : total - active})
              </button>
            ))}
            <input
              className="input-field"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search email or name…"
              style={{ flex: 1, minWidth: 160, fontSize: '.82rem', padding: '6px 12px' }}
            />
          </div>

          <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
            {display.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#555' }}>No subscribers found</div>
            )}
            {display.map(sub => (
              <div key={sub.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,.04)', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '.88rem', color: sub.active ? '#ccc' : '#555' }}>{sub.email}</span>
                    {sub.name && <span style={{ fontSize: '.72rem', color: '#444' }}>{sub.name}</span>}
                    {!sub.active && (
                      <span style={{ fontSize: '.62rem', color: '#e05050', background: 'rgba(224,80,80,.08)', padding: '1px 8px', borderRadius: 10, fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700 }}>unsub</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '.7rem', color: '#444' }}>{SOURCE_LABELS[sub.source] || sub.source || '—'}</span>
                    <span style={{ fontSize: '.7rem', color: sub.gdpr_consent ? '#4cca74' : '#e05050' }}>
                      {sub.gdpr_consent ? '✓ GDPR' : '✗ No consent'}
                    </span>
                    {sub.gdpr_consent_at && (
                      <span style={{ fontSize: '.68rem', color: '#333' }}>
                        {new Date(sub.gdpr_consent_at).toLocaleDateString()}
                      </span>
                    )}
                    <span style={{ fontSize: '.68rem', color: '#333' }}>{sub.country} · {sub.language?.toUpperCase()}</span>
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  {sub.active ? (
                    <button onClick={() => unsubscribe(sub.id, sub.email)} style={{ background: 'none', border: '1px solid rgba(224,80,80,.2)', borderRadius: 20, padding: '4px 12px', color: '#e05050', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 700, cursor: 'pointer' }}>
                      Unsub
                    </button>
                  ) : (
                    <button onClick={() => resubscribe(sub.id, sub.email)} style={{ background: 'none', border: '1px solid rgba(76,202,116,.2)', borderRadius: 20, padding: '4px 12px', color: '#4cca74', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem', fontWeight: 700, cursor: 'pointer' }}>
                      Re-sub
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}